import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { } from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { RootState } from "../../../store/store";
import { GetPolicyRisks } from "../../../store/user/policyDetails/policyDetailsActions";
import { IPolicyVehicle } from "../../../models/user/policyDetails/PolicyDetails";
import { UpdateGarageLocationPanel } from "./UpdateGarageLocationPanel";
import { renderAddress } from "../../PolicyDetails/policyTabComponents/VehiclesAnDrivers/Vehicles/Vehicles";

interface PolicyAction {
  id: string;
  label: string;
  params?: any;
  followup_message?: string;
}

interface ChangeGarageLocationCardProps {
  card: any;
  onAction?: (action: PolicyAction) => void;
  policyId?: string;
  carrier?: string;
  lobCode?: string;
  system?: string;
  policyNumber?: string;
}

const ChangeGarageLocationCard: React.FC<ChangeGarageLocationCardProps> = ({
  card,
  policyId,
  carrier,
  lobCode,
  system,
  policyNumber,
  onAction,
}) => {
  const dispatch = useAppDispatch();
  const { policyVehicles } = useAppSelector(
    (state: RootState) => state.policyDetails
  );
  
  const refs = useMemo(() => card?.entity_refs || {}, [card]);
  const effectivePolicyId = policyId || refs.policy_id || refs.policyId;
  const effectiveCarrier = carrier || refs.carrier;
  const effectiveLob = lobCode || refs.lob_code || refs.lobCode;
  const effectiveSystem = system || refs.system;

  useEffect(() => {
    if (effectivePolicyId && effectiveCarrier && effectiveSystem && effectiveLob) {
      dispatch(
        GetPolicyRisks({
          policyId: String(effectivePolicyId),
          carrier: String(effectiveCarrier),
          system: String(effectiveSystem),
          lobCode: String(effectiveLob),
        })
      );
    }
  }, [dispatch, effectivePolicyId, effectiveCarrier, effectiveSystem, effectiveLob]);

  const [selectedVehicle, setSelectedVehicle] = useState<IPolicyVehicle | null>(
    null
  );
  const [showForm, setShowForm] = useState<boolean>(false);

  const handleOpenFormForVehicle = useCallback((vehicle: IPolicyVehicle) => {
    setSelectedVehicle(vehicle);
    setShowForm(true);
  }, []);

  const handleFollowup = useCallback(
    (action: { followup_message?: string; label?: string }) => {
      if (onAction) {
        onAction({ id: "followup", label: action.label ?? "Follow up", followup_message: action.followup_message });
      }
    },
    [onAction]
  );

  const handleCancel = useCallback(() => {
    setShowForm(false);
    setSelectedVehicle(null);
  }, []);

  if (!card) return null;

  // If user chose to edit, show the inline panel (right-side form)
  if (showForm && selectedVehicle) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: { xs: "100%", md: "100%" },
          border: "1px solid #e5e7eb",
          borderRadius: { xs: 1, md: 2 },
          p: { xs: 1, md: 1.5 },
          bgcolor: "#ffffff",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>
              {"Update Garage Location"}
            </Typography>
          </Box>
          <UpdateGarageLocationPanel
            vehicle={selectedVehicle}
            policyNumber={policyNumber}
            onAction={handleFollowup}
            onCancel={handleCancel}
          />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: { xs: "100%", md: "100%" },
        border: "1px solid #e5e7eb",
        borderRadius: { xs: 1, md: 2 },
        p: { xs: 1, md: 1.5 },
        bgcolor: "#ffffff",
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: { xs: 12, md: 14 } }}>
            {card?.title || "Change Garage Location"}
          </Typography>
          {card?.subtitle && (
            <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>
              {card.subtitle}
            </Typography>
          )}
        </Box>

        {(policyVehicles || []).map((vehicle: IPolicyVehicle, iIdx: number) => (
          <Paper
            elevation={0}
            key={`vehicle_${iIdx}`}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: { xs: 1, md: 2 },
              p: { xs: 1, md: 1.25 },
              bgcolor: "#ffffff",
              transition:
                "background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 11, md: 12 },
                    color: "#111827",
                    fontWeight: 700,
                  }}
                >
                  {`${vehicle.manufactureYr}-${vehicle.make}-${vehicle.model}`}
                </Typography>
                {vehicle?.vin && (
                  <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280" }}>
                    {vehicle.vin}
                  </Typography>
                )}
                <Typography sx={{ fontSize: { xs: 10, md: 12 }, color: "#6b7280", mt: 0.5 }}>
                  Garage Location: {renderAddress(vehicle?.address as any)}
                </Typography>
              </Box>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleOpenFormForVehicle(vehicle)}
                sx={{
                  textTransform: "none",
                  fontSize: { xs: 10, md: 12 },
                }}
              >
                Update
              </Button>
            </Box>
          </Paper>
        ))}

         {/* Inline panel replaces drawer; nothing additional to render here */}
      </Box>
    </Paper>
  );
};

export default ChangeGarageLocationCard;


